import { authHelper } from '../utils/authHelper';
import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  CreditCard,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Gift
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export function AdminOverview({ stats }) {
  const [recentActivity, setRecentActivity] = useState([]);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    fetchRecentActivity();
    fetchChartData();
  }, []);

  const fetchRecentActivity = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/activity`, {
        headers: {
          'Authorization': `Bearer ${authHelper.getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRecentActivity(data.activity || []);
      }
    } catch (error) {
      console.error('Error fetching activity:', error);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/revenue-chart`, {
        headers: {
          'Authorization': `Bearer ${authHelper.getToken()}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setChartData(data.chart || []);
      }
    } catch (error) {
      console.error('Error fetching chart data:', error);
    }
  };

  const statCards = [
    {
      title: 'Total Usuarios',
      value: stats.totalUsers || 0,
      icon: Users,
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/20'
    },
    {
      title: 'Suscripciones Activas',
      value: stats.activeSubscriptions || 0,
      icon: CheckCircle2,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20'
    },
    {
      title: 'Acceso Gratis',
      value: stats.freeAccessUsers || 0,
      icon: Gift,
      color: 'text-purple-500',
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/20'
    },
    {
      title: 'En Período de Prueba',
      value: stats.trialUsers || 0,
      icon: Clock,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10',
      border: 'border-amber-500/20'
    },
    {
      title: 'Ingresos Totales',
      value: `$${stats.revenue?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'text-[#C5A14C]',
      bg: 'bg-[#C5A14C]/10',
      border: 'border-[#C5A14C]/20'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">Panel General</h2>
        <p className="text-white/60">Resumen y métricas principales de MÉTODO 11</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <div
              key={stat.title}
              className={`${stat.bg} ${stat.border} border rounded-xl p-6 backdrop-blur-md`}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon className={`w-8 h-8 ${stat.color}`} />
              </div>
              <p className="text-sm text-white/60 mb-1">{stat.title}</p>
              <p className={`text-3xl font-black ${stat.color}`}>{stat.value}</p>
            </div>
          );
        })}
      </div>

      {/* Revenue Chart */}
      {chartData.length > 0 && (
        <div className="bg-white/5 rounded-xl p-6 border border-white/10">
          <h3 className="text-lg font-semibold text-white mb-4">Ingresos Mensuales</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis dataKey="month" stroke="rgba(255,255,255,0.5)" />
              <YAxis stroke="rgba(255,255,255,0.5)" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#1E3A8A', 
                  border: '1px solid rgba(197,161,76,0.3)',
                  borderRadius: '8px'
                }} 
              />
              <Bar dataKey="revenue" fill="#C5A14C" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Recent Activity */}
      <div className="bg-white/5 rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Actividad Reciente</h3>
        <div className="space-y-3">
          {recentActivity.length > 0 ? (
            recentActivity.slice(0, 10).map((activity, idx) => (
              <div key={`${activity.type}-${activity.timestamp || activity.date || idx}`} className="flex items-center gap-4 p-3 bg-white/5 rounded-lg">
                {activity.type === 'new_user' && <Users className="w-5 h-5 text-blue-500" />}
                {activity.type === 'payment' && <CreditCard className="w-5 h-5 text-emerald-500" />}
                {activity.type === 'subscription_expired' && <XCircle className="w-5 h-5 text-red-500" />}
                
                <div className="flex-1">
                  <p className="text-white text-sm">{activity.description}</p>
                  <p className="text-white/40 text-xs">{activity.timestamp}</p>
                </div>
              </div>
            ))
          ) : (
            <p className="text-white/40 text-center py-8">No hay actividad reciente</p>
          )}
        </div>
      </div>
    </div>
  );
}
